/**
 * Airbnb link provider.
 *
 * Airbnb refuses the sync Lambda's preview fetch, so a bookmark falls back to a
 * card titled "airbnb.com/rooms", and a link pasted inline stays 200+ characters
 * of tracking parameters. Everything worth showing (listing, dates, guest count)
 * is already in the URL, so parse it out. No network, no scraping.
 *
 * See richLinks.ts for the rendering this feeds.
 */
import { registerProvider, type RichLink } from './richLinks';

export type AirbnbKind = 'stay' | 'experience' | 'search' | 'other';

export interface AirbnbLink {
  kind: AirbnbKind;
  /** Numeric listing id, when the URL points at one specific place. */
  listingId?: string;
  /** Place name, for search URLs and host-shared vanity links. */
  place?: string;
  /** YYYY-MM-DD, exactly as Airbnb puts it in the query string. */
  checkIn?: string;
  checkOut?: string;
  nights?: number;
  guests?: number;
}

function isAirbnbHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  // airbnb.com, airbnb.co.uk, www.airbnb.fr … plus the abnb.me share shortener.
  return /(^|\.)airbnb\.[a-z]{2,3}(\.[a-z]{2,3})?$/.test(host) || /(^|\.)abnb\.me$/.test(host);
}

/** "tulum--mexico" / "casa-del-mar" → "Tulum, Mexico" / "Casa Del Mar". */
function prettifySlug(slug: string): string {
  let text: string;
  try {
    text = decodeURIComponent(slug);
  } catch {
    text = slug;
  }
  return text
    .replace(/--/g, ', ')
    .replace(/[-_+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/(^|[\s,])([a-z])/g, (_m, lead: string, c: string) => lead + c.toUpperCase())
    .slice(0, 60);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readDate(params: URLSearchParams, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = params.get(k);
    if (v && ISO_DATE.test(v)) return v;
  }
  return undefined;
}

function readCount(params: URLSearchParams, key: string): number {
  const n = Number(params.get(key));
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Midnight UTC, so a YYYY-MM-DD never slips a day in a western timezone. */
function toUtcDate(iso: string): Date | null {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseAirbnbUrl(raw: string): AirbnbLink | null {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (!isAirbnbHost(u.hostname)) return null;

  const segs = u.pathname.split('/').filter(Boolean);
  // Some localized links carry a leading language segment (/en/rooms/123).
  if (segs.length > 1 && /^[a-z]{2}(-[a-z]{2})?$/i.test(segs[0])) segs.shift();

  const link: AirbnbLink = { kind: 'other' };

  const first = (segs[0] ?? '').toLowerCase();
  if (first === 'rooms' || first === 'luxury') {
    link.kind = 'stay';
    // /rooms/123, /rooms/plus/123 and /luxury/listing/123 all end in the id.
    link.listingId = segs.slice(1).reverse().find((s) => /^\d+$/.test(s));
  } else if (first === 'experiences') {
    link.kind = 'experience';
    link.listingId = segs.slice(1).find((s) => /^\d+$/.test(s));
  } else if (first === 's') {
    link.kind = 'search';
    if (segs[1] && segs[1].toLowerCase() !== 'homes') link.place = prettifySlug(segs[1]);
  } else if (first === 'h' && segs[1]) {
    // Host-shared vanity link: /h/casa-del-mar.
    link.kind = 'stay';
    link.place = prettifySlug(segs[1]);
  }

  const p = u.searchParams;
  // Listing pages use check_in/check_out; search pages use checkin/checkout.
  link.checkIn = readDate(p, 'check_in', 'checkin');
  link.checkOut = readDate(p, 'check_out', 'checkout');

  const inDate = link.checkIn ? toUtcDate(link.checkIn) : null;
  const outDate = link.checkOut ? toUtcDate(link.checkOut) : null;
  if (inDate && outDate) {
    const nights = Math.round((outDate.getTime() - inDate.getTime()) / 86_400_000);
    if (nights > 0) link.nights = nights;
  }

  // Airbnb sometimes sends a rolled-up `guests` and sometimes the breakdown.
  // Infants and pets do not count toward Airbnb's guest total, so they are left
  // out here rather than inflating the number.
  const guests = readCount(p, 'guests') || readCount(p, 'adults') + readCount(p, 'children');
  if (guests > 0) link.guests = guests;

  return link;
}

function formatDay(iso: string): string {
  const d = toUtcDate(iso);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Aug 3 – 7" when the month is shared, "Aug 30 – Sep 2" when it is not. */
function formatStayDates(checkIn?: string, checkOut?: string): string {
  if (checkIn && checkOut) {
    const start = formatDay(checkIn);
    const end = checkIn.slice(0, 7) === checkOut.slice(0, 7)
      ? String(toUtcDate(checkOut)?.getUTCDate() ?? '')
      : formatDay(checkOut);
    return start && end ? `${start} – ${end}` : start || end;
  }
  if (checkIn) return `From ${formatDay(checkIn)}`;
  if (checkOut) return `Until ${formatDay(checkOut)}`;
  return '';
}

/** The chips under the title: dates, nights, guests — whatever the URL knew. */
function airbnbDetails(link: AirbnbLink): string[] {
  const out: string[] = [];
  const dates = formatStayDates(link.checkIn, link.checkOut);
  if (dates) out.push(dates);
  if (link.nights) out.push(`${link.nights} night${link.nights === 1 ? '' : 's'}`);
  if (link.guests) out.push(`${link.guests} guest${link.guests === 1 ? '' : 's'}`);
  return out;
}

// Deliberately brand-free: every rendering pairs the title with an "Airbnb"
// badge, and "Airbnb — Airbnb stay" reads like a stutter.
const KIND_LABEL: Record<AirbnbKind, string> = {
  stay: 'Stay',
  experience: 'Experience',
  search: 'Home search',
  other: 'Listing',
};

/** Best title we can offer when the page itself told us nothing. */
function airbnbTitle(link: AirbnbLink): string {
  if (link.place) {
    return link.kind === 'search' ? `Homes in ${link.place}` : link.place;
  }
  // The listing id is opaque, but it is the only thing that tells two different
  // stays in the same itinerary apart.
  if (link.listingId) return `${KIND_LABEL[link.kind]} · #${link.listingId}`;
  return KIND_LABEL[link.kind];
}

registerProvider((url, raw): RichLink | null => {
  if (!isAirbnbHost(url.hostname)) return null;
  const link = parseAirbnbUrl(raw);
  if (!link) return null;
  return {
    brand: 'Airbnb',
    variant: 'is-airbnb',
    title: airbnbTitle(link),
    details: airbnbDetails(link),
  };
});
